import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SCREEN_SIZE } from 'types';
import { selectScreenSize } from 'redux/session/selectors';
import {
  Button,
  TextInput,
  Form,
  Link,
  InlineLoading
} from 'carbon-components-react';
import { ArrowRight16, ArrowLeft16 } from '@carbon/icons-react';
import { useFormik } from 'formik';
import { FormError } from 'components';
import Utils from 'utilities/helper.util';
import * as Yup from 'yup';
import { ElevenHundredDetailsFormValues } from '../localStore/types';

const Schema = Yup.object().shape({
  codes: Yup.string().min(6).required()
});

const VerifyEmail: React.FC<Props> = ({
  onSubmit,
  onClickBack,
  elevenHundredDetailsValues,
  onClickRequestNewCode,
  isVerifyingUserLinking,
  verifyUserLinkingError,
  onClickCancel
}) => {
  const screenSize = useSelector(selectScreenSize);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(true);
  const [requestNewCodeLinkDisabled, setRequestNewCodeLinkDisabled] =
    useState<boolean>(false);
  const [showAPIError, setShowAPIError] = useState<boolean>(false);
  const [translatedAPIErrorMessage, setTranslatedAPIErrorMessage] =
    useState<string>('');

  const {
    touched,
    errors,
    values,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    handleBlur
  } = useFormik<ElevenHundredDetailsFormValues>({
    enableReinitialize: true,
    initialValues: {
      codes: ''
    },
    validationSchema: Schema,
    onSubmit: (values: ElevenHundredDetailsFormValues) => {
      const { codes } = values;
      onSubmit({ codes });
    }
  });

  const handleTextInputChange = (val: string) => {
    setFieldTouched('codes', true, true);
    setFieldValue('codes', val.toUpperCase());
    setShowAPIError(false);
  };

  const clearInputFields = () => {
    setFieldValue('codes', '');
  };

  const handleReqNewCode = () => {
    setShowAPIError(false);
    onClickRequestNewCode();
    setRequestNewCodeLinkDisabled(true);
    clearInputFields();
    setTimeout(() => {
      setRequestNewCodeLinkDisabled(false);
    }, 30000);
  };

  const translateAPIErrorMessage = (apiError: string) => {
    switch (apiError) {
      case 'VERIFICATION_CODE_IS_INVALID':
        return 'Verification code is invalid';
      case 'EXISTING_SUBSCRIPTION_ERROR':
        return 'Existing subscription error';
      case 'NO_MATCHING_TOKEN_FOUND':
        return 'Verification token has expired';
      case 'Event object failed validation':
        return 'Verification code is invalid';
      default:
        return apiError;
    }
  };

  useEffect(() => {
    if (Utils.isObjectEmpty(errors) && values.codes) {
      setNextButtonDisabled(false);
    } else {
      setNextButtonDisabled(true);
    }
  }, [errors, touched, values]);

  useEffect(() => {
    if (
      verifyUserLinkingError &&
      verifyUserLinkingError !== 'EXISTING_SUBSCRIPTION_ERROR'
    ) {
      setShowAPIError(true);
      setTranslatedAPIErrorMessage(
        translateAPIErrorMessage(verifyUserLinkingError)
      );
    } else {
      setShowAPIError(false);
    }
  }, [verifyUserLinkingError]);

  return (
    <Form onSubmit={handleSubmit}>
      <div className="form-fields-wrapper">
        <div className={`textinput-fields-wrapper padding step3`}>
          <div className="title">Verification code</div>
          <div id="codeInputSection" className="form-fields">
            <TextInput
              id="codes"
              name="codes"
              className="code-input"
              labelText=""
              onChange={(val) => handleTextInputChange(val.target.value)}
              onBlur={handleBlur}
              value={values.codes}
              size={'lg'}
              maxLength={6}
            />
          </div>
          {verifyUserLinkingError && showAPIError && (
            <FormError>{translatedAPIErrorMessage}</FormError>
          )}
          <div className="helper-text">
            Enter the verification code we sent to {elevenHundredDetailsValues}
          </div>
        </div>
      </div>
      {screenSize === SCREEN_SIZE.SM && <div className="divider"></div>}
      <div className="request-new-code padding">
        <Link
          onClick={handleReqNewCode}
          disabled={requestNewCodeLinkDisabled || isVerifyingUserLinking}
        >
          Request new code
        </Link>
      </div>
      <div id="formButtons" className="form-buttons-wrapper-step2 padding">
        <div className="form-buttons-wrapper-step2-line1">
          {screenSize !== SCREEN_SIZE.SM && (
            <Button
              kind="tertiary"
              size={'md'}
              renderIcon={ArrowLeft16}
              hasIconOnly={true}
              className="back-button"
              onClick={() => onClickBack()}
              iconDescription="Back"
              disabled={isVerifyingUserLinking}
            />
          )}
          <Button
            kind="tertiary"
            size={'md'}
            className="cancel-button"
            disabled={isVerifyingUserLinking}
            onClick={() => onClickCancel()}
          >
            Cancel
          </Button>
        </div>
        <div className="form-buttons-wrapper-step2-line2">
          <Button
            kind="secondary"
            type="submit"
            size={'md'}
            renderIcon={isVerifyingUserLinking ? '' : ArrowRight16}
            className="next-button"
            disabled={nextButtonDisabled || isVerifyingUserLinking}
          >
            {!isVerifyingUserLinking ? (
              'Next'
            ) : (
              <InlineLoading
                description={!isVerifyingUserLinking ? 'Next' : 'Submitting...'}
                status={!isVerifyingUserLinking ? 'finished' : 'active'}
                aria-live={'assertive'}
              />
            )}
          </Button>
        </div>
      </div>
    </Form>
  );
};

type Props = {
  onSubmit: Function;
  onClickBack: Function;
  elevenHundredDetailsValues: string;
  onClickRequestNewCode: Function;
  isVerifyingUserLinking?: boolean;
  verifyUserLinkingError?: any;
  onClickCancel: Function;
};

export default React.memo(VerifyEmail);
